import { format } from 'date-fns';
import {
  CalendarIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  FolderOpenIcon,
} from 'lucide-react';

import BlobPreview from '@/components/documents-v2/components/inspector/BlobPreview';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMatter } from '@/hooks/useMatter';
import { blobsApiClient } from '@/services/api/blobService';
import {
  TreatmentRecordRequestRecordSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

const RecordCard = ({
  record,
  matterId,
  index,
}: {
  record: TreatmentRecordRequestRecordSchema;
  matterId: string;
  index: number;
}) => {
  const { data: blob, isLoading: isBlobLoading } =
    blobsApiClient.useGetBlobDetailQuery(
      {
        matterId,
        blobId: record.blob_id,
      },
      {
        skip: !record.blob_id,
      },
    );

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium">
              {blob?.name || `Record #${index + 1}`}
            </CardTitle>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileIcon className="h-3.5 w-3.5" />
                <span>{formatFileSize(blob?.file_size)}</span>
              </div>
              <div className="flex items-center gap-1">
                <CalendarIcon className="h-3.5 w-3.5" />
                <span>
                  {format(new Date(record.created_at), 'MMM d, yyyy')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (blob?.download_url) {
                  window.open(blob.download_url, '_blank');
                }
              }}
              disabled={!blob?.download_url}
            >
              <ExternalLinkIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (blob?.download_url) {
                  const a = document.createElement('a');
                  a.href = blob.download_url;
                  a.download = blob.name || 'download';
                  a.click();
                }
              }}
              disabled={!blob?.download_url}
            >
              <DownloadIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {blob && (
        <CardContent className="pt-0">
          <div className="border rounded-lg overflow-hidden bg-muted/30">
            {isBlobLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div className="h-48">
                <BlobPreview file={blob} />
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const RecordsListSkeleton = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <Card key={i}>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-32 mt-1" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const EmptyState = () => (
  <Card className="border-dashed">
    <CardContent className="flex flex-col items-center justify-center py-12">
      <FolderOpenIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <h3 className="font-semibold text-lg mb-1">No Records Found</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        No medical records have been received for this request yet.
      </p>
    </CardContent>
  </Card>
);

export const RecordsList = ({
  request,
}: {
  request: TreatmentRecordRequestSchema;
}) => {
  const { matterId } = useMatter();

  const { data: records, isLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRecordsListQuery(
      {
        matterId,
        providerId: request.provider_id,
        requestId: request.id,
      },
    );

  if (isLoading) {
    return <RecordsListSkeleton />;
  }

  if (!records || records.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {records.length} {records.length === 1 ? 'Record' : 'Records'}
        </h3>
      </div>

      {records.map((record, index) => (
        <RecordCard
          key={record.id}
          record={record}
          matterId={matterId}
          index={index}
        />
      ))}
    </div>
  );
};
